import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  Optional,
  SkipSelf,
} from '@angular/core';
import {
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';

@Component({
  selector: 'app-input',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './input.component.html',
  styleUrl: './input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
})
export class InputComponent implements ControlValueAccessor {
  @Input() readonly: boolean = false;
  @Input() currentvalue: any = '';
  @Input() type: string = 'text';
  @Input() label: string = '';
  @Input() submitted: boolean = false;
  @Input() textarea: boolean = false;
  @Input() placeholder: string = '';
  @Input() disabled: boolean = false;
  @Input() is_required: boolean = false;
  @Output() action = new EventEmitter<void>();
  @Input() formControlName?: string; // مهم عشان نعرف اسم الكنترول
  @Input() maxlength?: number; // optional
  @Input() minlength?: number; // optional
  @Input() style?: {}; // optional
  togglePassword: boolean = false;

  constructor(@Optional() @SkipSelf() private controlContainer: ControlContainer) {}

  get control(): FormControl | null {
    return this.formControlName
      ? (this.controlContainer?.control?.get(this.formControlName) as FormControl)
      : null;
  }
  value: any = '';
  touched = false;

  // ControlValueAccessor methods
  onChange = (_: any) => {};
  onTouched = () => {};

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // this.disabled = isDisabled;
  }

  // Input change
  handleInput(event: any) {
    const val = event.target.value;
    this.value = val;
    this.onChange(val);
    this.onTouched();
  }

  // Trigger external action
  triggerAction() {
    this.action.emit();
  }

  handleFocus() {
    // ممكن نضيف أي لوجيك عند الـ focus هنا
  }

  handleBlur() {
    this.control?.markAsTouched();
  }

  get remainingChars(): number | null {
    if (!this.control || this.maxlength === undefined) return null;
    const remaining = this.maxlength - (this.control.value?.length || 0);
    return remaining >= 0 ? remaining : 0; // هنا بنمنع السالب
  }

  get inputType(): string {
    if (this.type === 'password') {
      return this.togglePassword ? 'text' : 'password';
    }
    return this.type;
  }

  togglePasswordVisibility() {
    this.togglePassword = !this.togglePassword;
  }

  get errorMessages(): string | string[] | null {
    const control = this.control;
    if (!control || !control.errors) return null;
    const errors = control.errors;
    const messages: string[] = [];
    const formattedName = this.formControlName
      ? this.customFormatControlName(this.formControlName)
      : '';
    if (errors['required']) {
      messages.push(`${formattedName} is required.`);
    }

    if (errors['pattern']) {
      if (this.type === 'url') {
        messages.push('Please enter a valid URL.');
      } else
        switch (this.formControlName) {
          case 'name':
            messages.push(`Must be 3–50 characters (letters and spaces only).`);
            break;
          case 'password':
            messages.push(
              `${formattedName} must be at least 8 characters and include uppercase, lowercase, number, and special character.`
            );
            break;
          case 'email':
            messages.push(`Please enter a valid ${formattedName}.`);
            break;
          case 'new_email':
            messages.push(`Please enter a valid ${formattedName}.`);
            break;
          case 'email_confirmation':
            messages.push(`Emails don’t match. Please try again.`);
            break;
          case 'username':
            messages.push(
              `Invalid username. Use 3–20 characters (letters, numbers, or underscores only).`
            );
            break;
          case 'new_password':
            messages.push(
              `8 characters or longer. Combine upper and lowercase letters and numbers.`
            );
            break;
          case 'new_password_confirmation':
            messages.push(`Passwords don’t match. Please try again.`);
            break;

          default:
            messages.push(`${formattedName} format is invalid.`);
        }
    }

    if (errors['email']) {
      messages.push(`${formattedName} must be a valid email.`);
    }

    if (errors['minlength'] && !errors['pattern']) {
      const requiredLength = errors['minlength'].requiredLength;
      messages.push(`${formattedName} must be at least ${requiredLength} characters long.`);
    }

    if (errors['maxlength'] && !errors['pattern']) {
      const requiredLength = errors['maxlength'].requiredLength;
      messages.push(`${formattedName} cannot exceed ${requiredLength} characters.`);
    }

    if (this.type === 'number' && errors['min']) {
      const min = errors['min'].min;
      messages.push(`${formattedName} must be greater than or equal to ${min}.`);
    }

    if (this.type === 'number' && errors['max']) {
      const max = errors['max'].max;
      messages.push(`${formattedName} must be less than or equal to ${max}.`);
    }

    if (errors['passwordMismatch']) {
      messages.push("Passwords don't match. Please try again.");
    }

    if (errors['passwordsMismatch']) {
      messages.push("Passwords don't match. Please try again.");
    }

    if (errors['emailMismatch']) {
      messages.push("Emails don't match. Please try again.");
    }

    if (messages.length === 0) return null;
    if (messages.length === 1) return messages[0];
    return messages;
  }

  private customFormatControlName(name: string): string {
    if (!name) return '';
    let formatted = name.replace(/([a-z])([A-Z])/g, '$1 $2');
    formatted = formatted.replace(/[_-]+/g, ' ');
    return formatted
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  get hasError(): boolean {
    return !!(this.control && this.control.invalid && this.submitted);
  }

  get isErrorString(): boolean {
    return typeof this.errorMessages === 'string';
  }

  get isErrorArray(): boolean {
    return Array.isArray(this.errorMessages);
  }

  trackByFn(index: number, item: string) {
    return item;
  }
}
